import { type Vec3 } from './vec3';

export type Mat4 = Float32Array;

export const m4 = (): Mat4 => {
  const m = new Float32Array(16);
  m[0] = 1;
  m[5] = 1;
  m[10] = 1;
  m[15] = 1;
  return m;
};

const SCRATCH: Mat4 = new Float32Array(16);

export const m4Identity = (out: Mat4): Mat4 => {
  out[0] = 1;
  out[1] = 0;
  out[2] = 0;
  out[3] = 0;
  out[4] = 0;
  out[5] = 1;
  out[6] = 0;
  out[7] = 0;
  out[8] = 0;
  out[9] = 0;
  out[10] = 1;
  out[11] = 0;
  out[12] = 0;
  out[13] = 0;
  out[14] = 0;
  out[15] = 1;
  return out;
};

export const m4Copy = (out: Mat4, a: Mat4): Mat4 => {
  for (let i = 0; i < 16; i++) out[i] = a[i]!;
  return out;
};

export const m4Mul = (out: Mat4, a: Mat4, b: Mat4): Mat4 => {
  // copy a in case out aliases a
  for (let i = 0; i < 16; i++) SCRATCH[i] = a[i]!;
  for (let j = 0; j < 4; j++) {
    for (let i = 0; i < 4; i++) {
      let v = 0;
      for (let k = 0; k < 4; k++) v += SCRATCH[k * 4 + i]! * b[j * 4 + k]!;
      out[j * 4 + i] = v;
    }
  }
  return out;
};

export const m4FromTranslation = (out: Mat4, x: number, y: number, z: number): Mat4 => {
  m4Identity(out);
  out[12] = x;
  out[13] = y;
  out[14] = z;
  return out;
};

export const m4FromScale = (out: Mat4, x: number, y: number, z: number): Mat4 => {
  out[0] = x;
  out[1] = 0;
  out[2] = 0;
  out[3] = 0;
  out[4] = 0;
  out[5] = y;
  out[6] = 0;
  out[7] = 0;
  out[8] = 0;
  out[9] = 0;
  out[10] = z;
  out[11] = 0;
  out[12] = 0;
  out[13] = 0;
  out[14] = 0;
  out[15] = 1;
  return out;
};

export const m4Compose = (
  out: Mat4,
  x: number,
  y: number,
  z: number,
  rotY: number,
  scale: number,
): Mat4 => {
  const c = Math.cos(rotY) * scale;
  const s = Math.sin(rotY) * scale;
  out[0] = c;
  out[1] = 0;
  out[2] = -s;
  out[3] = 0;
  out[4] = 0;
  out[5] = scale;
  out[6] = 0;
  out[7] = 0;
  out[8] = s;
  out[9] = 0;
  out[10] = c;
  out[11] = 0;
  out[12] = x;
  out[13] = y;
  out[14] = z;
  out[15] = 1;
  return out;
};

export const m4ComposeXYZ = (
  out: Mat4,
  x: number,
  y: number,
  z: number,
  rotY: number,
  sx: number,
  sy: number,
  sz: number,
): Mat4 => {
  const c = Math.cos(rotY);
  const s = Math.sin(rotY);
  out[0] = c * sx;
  out[1] = 0;
  out[2] = -s * sx;
  out[3] = 0;
  out[4] = 0;
  out[5] = sy;
  out[6] = 0;
  out[7] = 0;
  out[8] = s * sz;
  out[9] = 0;
  out[10] = c * sz;
  out[11] = 0;
  out[12] = x;
  out[13] = y;
  out[14] = z;
  out[15] = 1;
  return out;
};

export const m4FromRotationY = (out: Mat4, rad: number): Mat4 => {
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  out[0] = c;
  out[1] = 0;
  out[2] = -s;
  out[3] = 0;
  out[4] = 0;
  out[5] = 1;
  out[6] = 0;
  out[7] = 0;
  out[8] = s;
  out[9] = 0;
  out[10] = c;
  out[11] = 0;
  out[12] = 0;
  out[13] = 0;
  out[14] = 0;
  out[15] = 1;
  return out;
};

export const m4FromRotationX = (out: Mat4, rad: number): Mat4 => {
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  out[0] = 1;
  out[1] = 0;
  out[2] = 0;
  out[3] = 0;
  out[4] = 0;
  out[5] = c;
  out[6] = s;
  out[7] = 0;
  out[8] = 0;
  out[9] = -s;
  out[10] = c;
  out[11] = 0;
  out[12] = 0;
  out[13] = 0;
  out[14] = 0;
  out[15] = 1;
  return out;
};

export const m4FromRotationZ = (out: Mat4, rad: number): Mat4 => {
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  out[0] = c;
  out[1] = s;
  out[2] = 0;
  out[3] = 0;
  out[4] = -s;
  out[5] = c;
  out[6] = 0;
  out[7] = 0;
  out[8] = 0;
  out[9] = 0;
  out[10] = 1;
  out[11] = 0;
  out[12] = 0;
  out[13] = 0;
  out[14] = 0;
  out[15] = 1;
  return out;
};

// Inverse of a rigid transform (rotation 3x3 + translation, no scale/shear).
// inv = [R^T | -R^T * t].
export const m4InverseRigid = (out: Mat4, m: Mat4): Mat4 => {
  const r00 = m[0]!;
  const r10 = m[1]!;
  const r20 = m[2]!;
  const r01 = m[4]!;
  const r11 = m[5]!;
  const r21 = m[6]!;
  const r02 = m[8]!;
  const r12 = m[9]!;
  const r22 = m[10]!;
  const tx = m[12]!;
  const ty = m[13]!;
  const tz = m[14]!;
  out[0] = r00;
  out[1] = r01;
  out[2] = r02;
  out[3] = 0;
  out[4] = r10;
  out[5] = r11;
  out[6] = r12;
  out[7] = 0;
  out[8] = r20;
  out[9] = r21;
  out[10] = r22;
  out[11] = 0;
  out[12] = -(r00 * tx + r10 * ty + r20 * tz);
  out[13] = -(r01 * tx + r11 * ty + r21 * tz);
  out[14] = -(r02 * tx + r12 * ty + r22 * tz);
  out[15] = 1;
  return out;
};

export const m4Perspective = (
  out: Mat4,
  fovYRad: number,
  aspect: number,
  near: number,
  far: number,
): Mat4 => {
  const f = 1 / Math.tan(fovYRad / 2);
  const nf = 1 / (near - far);
  out[0] = f / aspect;
  out[1] = 0;
  out[2] = 0;
  out[3] = 0;
  out[4] = 0;
  out[5] = f;
  out[6] = 0;
  out[7] = 0;
  out[8] = 0;
  out[9] = 0;
  out[10] = (far + near) * nf;
  out[11] = -1;
  out[12] = 0;
  out[13] = 0;
  out[14] = 2 * far * near * nf;
  out[15] = 0;
  return out;
};

export const m4Ortho = (
  out: Mat4,
  left: number,
  right: number,
  bottom: number,
  top: number,
  near: number,
  far: number,
): Mat4 => {
  const lr = 1 / (left - right);
  const bt = 1 / (bottom - top);
  const nf = 1 / (near - far);
  out[0] = -2 * lr;
  out[1] = 0;
  out[2] = 0;
  out[3] = 0;
  out[4] = 0;
  out[5] = -2 * bt;
  out[6] = 0;
  out[7] = 0;
  out[8] = 0;
  out[9] = 0;
  out[10] = 2 * nf;
  out[11] = 0;
  out[12] = (left + right) * lr;
  out[13] = (top + bottom) * bt;
  out[14] = (far + near) * nf;
  out[15] = 1;
  return out;
};

export const m4LookAt = (out: Mat4, eye: Vec3, center: Vec3, up: Vec3): Mat4 => {
  const ex = eye[0]!;
  const ey = eye[1]!;
  const ez = eye[2]!;
  let fx = center[0]! - ex;
  let fy = center[1]! - ey;
  let fz = center[2]! - ez;
  let fl = Math.hypot(fx, fy, fz);
  if (fl === 0) {
    return m4Identity(out);
  }
  fl = 1 / fl;
  fx *= fl;
  fy *= fl;
  fz *= fl;

  const ux0 = up[0]!;
  const uy0 = up[1]!;
  const uz0 = up[2]!;
  let sx = fy * uz0 - fz * uy0;
  let sy = fz * ux0 - fx * uz0;
  let sz = fx * uy0 - fy * ux0;
  let sl = Math.hypot(sx, sy, sz);
  if (sl === 0) {
    sx = 0;
    sy = 0;
    sz = 0;
  } else {
    sl = 1 / sl;
    sx *= sl;
    sy *= sl;
    sz *= sl;
  }

  const ux = sy * fz - sz * fy;
  const uy = sz * fx - sx * fz;
  const uz = sx * fy - sy * fx;

  out[0] = sx;
  out[1] = ux;
  out[2] = -fx;
  out[3] = 0;
  out[4] = sy;
  out[5] = uy;
  out[6] = -fy;
  out[7] = 0;
  out[8] = sz;
  out[9] = uz;
  out[10] = -fz;
  out[11] = 0;
  out[12] = -(sx * ex + sy * ey + sz * ez);
  out[13] = -(ux * ex + uy * ey + uz * ez);
  out[14] = fx * ex + fy * ey + fz * ez;
  out[15] = 1;
  return out;
};
